import React from "react";
import { T } from "Components";

import useStatistics from "../../Infrastructure/useStatistics";
import {
  FormatCompactCurrency,
  FormatCompactLitre,
  FormatNumber,
  FormatPercent,
  FormatSignedPercent,
} from "../../Infrastructure/format";
import {
  AlertIcon,
  AnimalIcon,
  CheckIcon,
  MembersIcon,
  MilkIcon,
  MoneyIcon,
} from "./Icons";

/** Tek KPI kartı. `tone` ikon kutusu ve rozet rengini belirler. */
const Kpi = ({ label, tone = "primary", Icon, children, footer, alert }) => (
  <section className={`fdash__card fdash__kpi ${alert ? "fdash__card--alert" : ""}`}>
    <div className="fdash__kpi__head">
      <span className="fdash__kpi__label">{label}</span>
      <span
        className="fdash__kpi__icon"
        style={{ background: `var(--${tone}-soft)`, color: `var(--${tone})` }}
      >
        <Icon />
      </span>
    </div>
    {children}
    {footer}
  </section>
);

/** Rozet + açıklama satırı. */
const Trend = ({ text, tone, description }) => (
  <div className="fdash__kpi__foot">
    <span
      className="fdash__kpi__badge"
      style={{ background: `var(--${tone}-soft)`, color: `var(--${tone})` }}
    >
      {text}
    </span>
    <span>{description}</span>
  </div>
);

/** Artış yeşil, azalış kehribar — durum renkleri koyu temada token'dan gelir. */
const rateTone = (rate) => (Number(rate) < 0 ? "amber" : "teal");

const KpiSkeleton = () => (
  <>
    {Array.from({ length: 6 }).map((_, index) => (
      <section className="fdash__card fdash__kpi" key={index}>
        <div className="fdash__skeleton" style={{ height: 14, width: "55%" }} />
        <div className="fdash__skeleton" style={{ height: 31, marginTop: 18, width: "45%" }} />
        <div className="fdash__skeleton" style={{ height: 14, marginTop: 14, width: "70%" }} />
      </section>
    ))}
  </>
);

const KpiCards = () => {
  const { data, loading, error } = useStatistics("/Statistics/Summary");

  if (error) {
    return (
      <div className="fdash__kpis">
        <section className="fdash__card fdash__kpi" style={{ gridColumn: "1 / -1" }}>
          <div className="fdash__state fdash__state--error">
            {T.Main("Dashboard.Statistics.LoadError")}
          </div>
        </section>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="fdash__kpis">
        <KpiSkeleton />
      </div>
    );
  }

  const collected = FormatCompactCurrency(data.CollectedThisMonth);
  const outstanding = FormatCompactCurrency(data.OutstandingReceivable);
  const overdue = FormatCompactCurrency(data.OverdueOver90Days);
  const milk = FormatCompactLitre(data.MilkAmountThisMonth);

  return (
    <div className="fdash__kpis">
      <Kpi label={T.Main("Dashboard.Statistics.TotalMember")} tone="primary" Icon={MembersIcon}>
        <div className="fdash__kpi__value">{FormatNumber(data.TotalMemberCount)}</div>
        <Trend
          text={`+${FormatNumber(data.NewMemberCountThisYear)}`}
          tone="teal"
          description={T.Main("Dashboard.Statistics.NewRegistrationThisYear")}
        />
      </Kpi>

      <Kpi label={T.Main("Dashboard.Statistics.ActivePassiveMember")} tone="teal" Icon={CheckIcon}>
        <div className="fdash__kpi__value">
          {FormatNumber(data.ActiveMemberCount)}
          <em>/ {FormatNumber(data.PassiveMemberCount)}</em>
        </div>
        <div className="fdash__kpi__bar">
          <span style={{ width: `${Math.min(Math.max(data.ActiveMemberRate, 0), 100)}%` }} />
          <span />
        </div>
        <div className="fdash__kpi__foot">
          {`${FormatPercent(data.ActiveMemberRate)} ${T.Main(
            "Dashboard.Statistics.ActivityRate",
          )}`}
        </div>
      </Kpi>

      <Kpi label={T.Main("Dashboard.Statistics.TotalAnimal")} tone="primary" Icon={AnimalIcon}>
        <div className="fdash__kpi__value">{FormatNumber(data.TotalAnimalCount)}</div>
        <Trend
          text={FormatSignedPercent(data.AnimalChangeRate)}
          tone={rateTone(data.AnimalChangeRate)}
          description={T.Main("Dashboard.Statistics.ComparedToLastQuarter")}
        />
      </Kpi>

      <Kpi label={T.Main("Dashboard.Statistics.CollectedThisMonth")} tone="teal" Icon={MoneyIcon}>
        <div className="fdash__kpi__value">
          {collected.value}
          {collected.unit && <small>{collected.unit}</small>}
        </div>
        <Trend
          text={FormatPercent(data.CollectionRealizationRate)}
          tone="teal"
          description={T.Main("Dashboard.Statistics.CollectionRealizationRate")}
        />
      </Kpi>

      <Kpi
        label={T.Main("Dashboard.Statistics.OutstandingReceivable")}
        tone="red"
        Icon={AlertIcon}
        alert
      >
        <div className="fdash__kpi__value" style={{ color: "var(--red)" }}>
          {outstanding.value}
          {outstanding.unit && <small style={{ color: "inherit" }}>{outstanding.unit}</small>}
        </div>
        <Trend
          text={`${overdue.value}${overdue.unit ? ` ${overdue.unit}` : ""}`}
          tone="red"
          description={T.Main("Dashboard.Statistics.Overdue90Plus")}
        />
      </Kpi>

      <Kpi label={T.Main("Dashboard.Statistics.MonthlyMilk")} tone="primary" Icon={MilkIcon}>
        <div className="fdash__kpi__value">
          {milk.value}
          <small>{milk.unit}</small>
        </div>
        <Trend
          text={FormatSignedPercent(data.MilkChangeRate)}
          tone={rateTone(data.MilkChangeRate)}
          description={T.Main("Dashboard.Statistics.ComparedToLastMonth")}
        />
      </Kpi>
    </div>
  );
};

export default KpiCards;
